/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
console.log("Hello World! (from sd-viblock-sd-product-images block)");
/* eslint-enable no-console */
class ImageGridSlider {
	constructor(container) {
		this.container = container;
		this.track = container.querySelector('.image-grid-track');
		this.items = Array.from(container.querySelectorAll('.image-grid-item'));
		this.prevBtn = container.querySelector('.slider-prev');
		this.nextBtn = container.querySelector('.slider-next');
		this.dotsContainer = container.querySelector('.slider-dots');

		this.currentIndex = 0;
		this.isAnimating = false;
		this.touchStartX = 0;
		this.touchEndX = 0;
		this.isMobile = false;

		this.init();
	}

	init() {
		this.checkMobile();
		this.createDots();
		this.attachEventListeners();

		// Check on resize
		window.addEventListener('resize', () => {
			this.checkMobile();
		});
	}

	checkMobile() {
		const wasMobile = this.isMobile;
		this.isMobile = window.innerWidth < 768;

		if (wasMobile !== this.isMobile) {
			if (this.isMobile) {
				this.setupMobileSlider();
			} else {
				this.teardownMobileSlider();
			}
		}
	}

	setupMobileSlider() {
		this.updateSlider();
		this.updateButtons();
	}

	teardownMobileSlider() {
		this.track.style.transform = '';
		this.currentIndex = 0;
		this.updateDots();
	}

	createDots() {
		if (!this.dotsContainer || this.items.length <= 1) return;

		this.dotsContainer.innerHTML = '';
		this.items.forEach((_, index) => {
			const dot = document.createElement('button');
			dot.className = 'slider-dot';
			dot.setAttribute('aria-label', `Go to image ${index + 1}`);
			if (index === 0) dot.classList.add('active');
			dot.addEventListener('click', () => this.goToSlide(index));
			this.dotsContainer.appendChild(dot);
		});
	}

	attachEventListeners() {
		if (this.prevBtn) {
			this.prevBtn.addEventListener('click', () => this.prev());
		}

		if (this.nextBtn) {
			this.nextBtn.addEventListener('click', () => this.next());
		}

		// Touch events
		this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
		this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
		this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e));

		// Keyboard navigation
		this.container.addEventListener('keydown', (e) => {
			if (!this.isMobile) return;

			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				this.prev();
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				this.next();
			}
		});
	}

	handleTouchStart(e) {
		if (!this.isMobile) return;
		this.touchStartX = e.touches[0].clientX;
	}

	handleTouchMove(e) {
		if (!this.isMobile) return;
		this.touchEndX = e.touches[0].clientX;
	}

	handleTouchEnd(e) {
		if (!this.isMobile) return;

		const diff = this.touchStartX - this.touchEndX;
		const threshold = 50;

		if (Math.abs(diff) > threshold) {
			if (diff > 0) {
				this.next();
			} else {
				this.prev();
			}
		}

		this.touchStartX = 0;
		this.touchEndX = 0;
	}

	prev() {
		if (this.isAnimating || !this.isMobile) return;

		if (this.currentIndex > 0) {
			this.goToSlide(this.currentIndex - 1);
		}
	}

	next() {
		if (this.isAnimating || !this.isMobile) return;

		if (this.currentIndex < this.items.length - 1) {
			this.goToSlide(this.currentIndex + 1);
		}
	}

	goToSlide(index) {
		if (this.isAnimating || !this.isMobile || index === this.currentIndex) return;

		this.currentIndex = Math.max(0, Math.min(index, this.items.length - 1));
		this.updateSlider();
		this.updateButtons();
		this.updateDots();
	}

	updateSlider() {
		if (!this.isMobile) return;

		this.isAnimating = true;
		this.track.classList.add('animating');

		const offset = -this.currentIndex * 100;
		this.track.style.transform = `translateX(${offset}%)`;

		setTimeout(() => {
			this.isAnimating = false;
			this.track.classList.remove('animating');
		}, 300);
	}

	updateButtons() {
		if (!this.prevBtn || !this.nextBtn) return;

		this.prevBtn.disabled = this.currentIndex === 0;
		this.nextBtn.disabled = this.currentIndex === this.items.length - 1;
	}

	updateDots() {
		if (!this.dotsContainer) return;

		const dots = this.dotsContainer.querySelectorAll('.slider-dot');
		dots.forEach((dot, index) => {
			dot.classList.toggle('active', index === this.currentIndex);
		});
	}
}

// Initialize all image grid sliders
document.addEventListener('DOMContentLoaded', () => {
	const containers = document.querySelectorAll('.wp-block-custom-image-grid');
	containers.forEach(container => {
		new ImageGridSlider(container);
	});
});

// Handle dynamic content loading (e.g., AJAX)
if (typeof window.addEventListener !== 'undefined') {
	window.addEventListener('load', () => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						const containers = node.querySelectorAll ?
							node.querySelectorAll('.wp-block-custom-image-grid') :
							[];
						containers.forEach(container => {
							if (!container.dataset.initialized) {
								new ImageGridSlider(container);
								container.dataset.initialized = 'true';
							}
						});
					}
				});
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});
	});
}

// lightbox code
document.addEventListener('DOMContentLoaded', function () {
	const imageGrids = document.querySelectorAll('.wp-block-custom-image-grid');

	imageGrids.forEach(function (grid) {
		const lightbox = grid.querySelector('.image-lightbox');
		const lightboxImage = lightbox.querySelector('.lightbox-image');
		const lightboxContent = lightbox.querySelector('.lightbox-image-caption');
		const closeBtn = lightbox.querySelector('.lightbox-close');
		const prevBtn = lightbox.querySelector('.lightbox-prev');
		const nextBtn = lightbox.querySelector('.lightbox-next');
		const triggers = grid.querySelectorAll('.image-lightbox-trigger');

		let currentIndex = 0;
		let allImages = [];

		// Collect all image data
		triggers.forEach(function (trigger) {
			allImages.push({
				url: trigger.dataset.fullImage,
				alt: trigger.dataset.alt,
				index: parseInt(trigger.dataset.index)
			});
		});

		function openLightbox(index) {
			currentIndex = index;
			const image = allImages[currentIndex];

			lightboxImage.src = image.url;
			lightboxImage.alt = image.alt;
			lightboxContent.innerHTML = image.alt;

			lightbox.classList.add('active');
			lightbox.setAttribute('aria-hidden', 'false');
			document.body.style.overflow = 'hidden';

			// Update navigation buttons visibility
			updateNavButtons();

			// Focus on close button for accessibility
			closeBtn.focus();
		}

		function closeLightbox() {
			lightbox.classList.remove('active');
			lightbox.setAttribute('aria-hidden', 'true');
			document.body.style.overflow = '';
		}

		function showNext() {
			if (currentIndex < allImages.length - 1) {
				openLightbox(currentIndex + 1);
			}
		}

		function showPrev() {
			if (currentIndex > 0) {
				openLightbox(currentIndex - 1);
			}
		}

		function updateNavButtons() {
			if (currentIndex === 0) {
				prevBtn.classList.add('hidden');
			} else {
				prevBtn.classList.remove('hidden');
			}

			if (currentIndex === allImages.length - 1) {
				nextBtn.classList.add('hidden');
			} else {
				nextBtn.classList.remove('hidden');
			}

			// Hide both if only one image
			if (allImages.length === 1) {
				prevBtn.classList.add('hidden');
				nextBtn.classList.add('hidden');
			}
		}

		// Event listeners
		triggers.forEach(function (trigger) {
			trigger.addEventListener('click', function () {
				const index = parseInt(this.dataset.index);
				openLightbox(index);
			});
		});

		closeBtn.addEventListener('click', closeLightbox);

		lightbox.querySelector('.lightbox-backdrop').addEventListener('click', closeLightbox);

		prevBtn.addEventListener('click', showPrev);
		nextBtn.addEventListener('click', showNext);

		// Keyboard navigation
		document.addEventListener('keydown', function (e) {
			if (!lightbox.classList.contains('active')) return;

			if (e.key === 'Escape') {
				closeLightbox();
			} else if (e.key === 'ArrowLeft') {
				showPrev();
			} else if (e.key === 'ArrowRight') {
				showNext();
			}
		});
	});
});
